import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { useDispatch } from 'react-redux';

import { isMobile } from '@common/utils/platform';
import { Modal } from '@common/ui/general';
import { ReferralContext } from '~referral/contexts';
import { REFERRAL_TRACKING } from '~referral/utils/trackingEvents';
import { SOCIAL_DATA } from '~referral/utils/constants';
import { useTranslations } from '@common/hooks';
import { useMoonshotAsset } from '@common/hooks/useMoonshot';
import { trackPostStatus } from '~referral/stores/referralCallback';
import HappyFlowShareableBodyUI from '~referral/ui/HappyFlowShareableBodyUI';
import NudgePostSharedModal from '~referral/components/NudgePostSharedModal';

const socialData = {
  twitter: {
    url: ({ url }) => `https://twitter.com/intent/tweet?url=${url}`,
  },
  linkedin: {
    url: ({ url }) => `https://www.linkedin.com/shareArticle?url=${url}`,
  },
  whatsapp: {
    url: ({ url }) => (isMobile() ? `https://wa.me/?text=${url}`
      : `https://web.whatsapp.com/send?text=${url}`),
  },
};

function HappyFlowShareableModal({
  onCloseModal, nudgeType, nudgeData, shouldFetchLinks = true,
}) {
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(true);
  const [shareBtnIsVisible, setShareBtnIsVisible] = useState(false);
  const [postShared, setPostShared] = useState(false);
  const [nudgesTranslator, setNudgesTranslator] = useState(null);

  const { analytics } = useContext(ReferralContext);
  const currentPagePath = window.location.pathname;
  const { translations } = useTranslations();

  const shouldFetch = shouldFetchLinks ? isOpen : shouldFetchLinks;

  let [isReady, sharableLinks] = useMoonshotAsset(
    nudgeData.sharable_links, shouldFetch,
  );

  if (!shouldFetchLinks && Object.keys(sharableLinks).length === 0) {
    isReady = true;
    sharableLinks = nudgeData.sharable_links;
  }

  const onPressPlatform = useCallback(() => {
    analytics.click(
      REFERRAL_TRACKING.clickReferralSharePlatforms,
      nudgeType, { pageSource: currentPagePath },
    );
    setShareBtnIsVisible(state => !state);
  }, [setShareBtnIsVisible, analytics, currentPagePath, nudgeType]);

  useEffect(() => {
    if (translations?.nudgesModal) {
      setNudgesTranslator(translations?.nudgesModal);
    }
  }, [translations]);

  useEffect(() => {
    if (nudgeType && currentPagePath) {
      analytics.view(
        REFERRAL_TRACKING.viewReferralNudgeHappyFlowModal,
        nudgeType, { pageSource: currentPagePath },
      );
    }
  }, [analytics, nudgeType, currentPagePath]);

  const onClose = useCallback(() => {
    setOpen(false);
    setPostShared(false);
    onCloseModal();
  }, [onCloseModal]);

  const handleKnowMoreRequest = useCallback(() => {
    analytics.click(
      REFERRAL_TRACKING.clickKnowMoreButton,
      'Happy Flow Shareable Modal',
    );
  }, [analytics]);

  const navigateToPlatform = useCallback((shareLink) => {
    if (navigator.canShare) {
      try {
        navigator.share({ url: shareLink });
      } catch (error) {
        // do nothing
      }
    } else {
      window.open(shareLink);
    }
  }, []);

  const handleRequest = useCallback(() => {
    analytics.click(
      REFERRAL_TRACKING.clickLinkedInShareButton,
      'Happy Flow Shareable Modal',
    );
    dispatch(trackPostStatus(nudgeType));
    setOpen(false);
    setPostShared(true);
    const shareLink = socialData.linkedin.url({ url: sharableLinks.linkedin });
    navigateToPlatform(shareLink);
  }, [analytics,
    dispatch, nudgeType, sharableLinks, navigateToPlatform]);

  const handleWhatsAppShareRequest = useCallback(() => {
    analytics.click(
      REFERRAL_TRACKING.clickWhatsappShareButton,
      'Streak Share Modal',
    );
    setPostShared(false);
    const shareLink = socialData.whatsapp.url({ url: sharableLinks.sharable });
    navigateToPlatform(shareLink);
  }, [navigateToPlatform, analytics, sharableLinks]);

  const handleTwitterShareRequest = useCallback(() => {
    analytics.click(
      REFERRAL_TRACKING.clickTwitterShareButton,
      'Streak Share Modal',
    );
    setPostShared(false);
    const shareLink = SOCIAL_DATA.twitter.url({ url: sharableLinks.twitter });
    navigateToPlatform(shareLink);
  }, [navigateToPlatform, analytics, sharableLinks]);


  return (
    !postShared ? (
      <div className="nudge-wrapper">
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          className="psp-shareable"
          withoutHeader
          // headerClassName="psp-shareable__header"
          // titleClassName="psp-shareable__title"
          containerClassName="psp-shareable__container"
        >
          <HappyFlowShareableBodyUI
            isReady={isReady}
            sharableLinks={sharableLinks}
            handleTwitterShareRequest={handleTwitterShareRequest}
            handleWhatsAppShareRequest={handleWhatsAppShareRequest}
            shareBtnIsVisible={shareBtnIsVisible}
            handleRequest={handleRequest}
            handleKnowMoreRequest={handleKnowMoreRequest}
            onPressPlatform={onPressPlatform}
            nudgesTranslator={nudgesTranslator}
          />

        </Modal>

      </div>
    ) : (
      <NudgePostSharedModal
        isOpen={postShared}
        analytics={analytics}
        onClose={onCloseModal}
        nudgeType={nudgeType}
      />
    )
  );
}

export default HappyFlowShareableModal;
